import { c as _c } from "react/compiler-runtime";
import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon } from "@website/icons/material";
import { Button } from "@website/ui/button";
import { MiniPopover } from "@website/ui/mini-popover";
import { Skeleton } from "@website/ui/skeleton";
import { StripFilterPanel, getStripFilterButtonClassName, getStripFilterContainerClassName, useStripFilterAnimation } from "@website/widget/filter/filter-components/strip";
import { getStripFilterOptionStaggerKey } from "@website/widget/filter/filter-components/strip.utils";
import { useHorizontalDragScroll } from "@website/widget/filter/hotel-filter-chips/horizontal-drag-scroll.hook";
import { HotelFilterChipsPropertiesTypes } from "@website/widget/filter/hotel-filter-chips/hotel-filter-chips.types";
import { useHotelFiltersChips } from "@website/widget/filter/hotel-filter-chips/hotel-filters-chips.hook";
import { useItemStagger } from "@website/widget/item-stagger/item-stagger.hook";
import { useRouter } from "next/router";
import { Fragment, MouseEvent, useEffect, useState } from "react";
const FILTER_CHIP_SKELETON_ITEM_KEYS = Array.from({
  length: 6
}, (_, index) => `filter-chip-skeleton-${index}`);
const STRIP_SKELETON_ITEM_KEYS = Array.from({
  length: 6
}, (_, index) => `strip-skeleton-${index}`);
const STRIP_ITEM_STAGGER_START_INDEX = STRIP_SKELETON_ITEM_KEYS.length;
const getStripItemStaggerIndexes = (chipsFiltersSchema: HotelFilterChipsPropertiesTypes["chipsFiltersSchema"]) => {
  const indexes: Record<string, number> = {};
  let index = STRIP_ITEM_STAGGER_START_INDEX;
  for (const [filterName, schema] of Object.entries(chipsFiltersSchema)) {
    if (schema.type !== "strip") continue;
    for (const option of schema.options || []) {
      indexes[getStripFilterOptionStaggerKey(filterName, option.name)] = index;
      index += 1;
    }
  }
  return indexes;
};
const Component = properties => {
  const $ = _c(63);
  const {
    chipsFiltersSchema,
    classname,
    hasFilter,
    hidePopoverActions,
    loading,
    isNewPlP,
    showStripLoadingSkeleton: t0,
    stripTriggerInView: t1,
    shouldAnimateStripItems: t2,
    shouldAnimateStripTrigger: t3
  } = properties;
  const showStripLoadingSkeleton = t0 === undefined ? true : t0;
  const stripTriggerInView = t1 === undefined ? true : t1;
  const shouldAnimateStripItems = t2 === undefined ? true : t2;
  const shouldAnimateStripTrigger = t3 === undefined ? true : t3;
  const {
    isMobile
  } = useViewPort();
  const horizontalDragScrollProperties = useHorizontalDragScroll();
  let t4;
  if ($[0] !== chipsFiltersSchema) {
    t4 = getStripItemStaggerIndexes(chipsFiltersSchema);
    $[0] = chipsFiltersSchema;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const stripItemStaggerIndexes = t4;
  let t5;
  if ($[2] !== loading || $[3] !== shouldAnimateStripItems || $[4] !== showStripLoadingSkeleton || $[5] !== stripItemStaggerIndexes) {
    t5 = shouldAnimateStripItems ? loading ? showStripLoadingSkeleton ? STRIP_SKELETON_ITEM_KEYS : [] : Object.keys(stripItemStaggerIndexes) : [];
    $[2] = loading;
    $[3] = shouldAnimateStripItems;
    $[4] = showStripLoadingSkeleton;
    $[5] = stripItemStaggerIndexes;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const stripStaggerItemKeys = t5;
  const t6 = loading ? "loading" : "loaded";
  let t7;
  if ($[7] !== t6) {
    t7 = {
      continueFromVisibleItems: true,
      refreshKey: t6
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const {
    getItemStaggerProperties
  } = useItemStagger(stripStaggerItemKeys, t7);
  const t8 = shouldAnimateStripItems ? getItemStaggerProperties : undefined;
  let t9;
  if ($[9] !== properties || $[10] !== stripItemStaggerIndexes || $[11] !== t8) {
    t9 = {
      ...properties,
      getStripItemStaggerProperties: t8,
      stripItemStaggerIndexes
    };
    $[9] = properties;
    $[10] = stripItemStaggerIndexes;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const {
    chips,
    children,
    defaultStripKey,
    onClick,
    onClose,
    onReset,
    onSubmit,
    onTextClick,
    buttonProperties
  } = useHotelFiltersChips(t9);
  const {
    closeVisibleStrip,
    entryDirection,
    handleTransitionEnd,
    isExiting: stripIsExiting,
    openStrip,
    renderedStripKey
  } = useStripFilterAnimation(defaultStripKey, stripTriggerInView);
  const [completedInitialStripKey, setCompletedInitialStripKey] = useState();
  const router = useRouter();
  let t10;
  if ($[13] !== router?.query) {
    t10 = router?.query.popover === "price_range" ? !Object?.keys(router?.query)?.includes(String("price_from")) && !Object?.keys(router?.query)?.includes(String("price_to")) : !Object?.keys(router?.query)?.includes(String(router?.query.popover));
    $[13] = router?.query;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const isResetDisabled = t10;
  const shouldReserveInitialStripHeight = isNewPlP && Boolean(defaultStripKey) && stripTriggerInView && completedInitialStripKey !== defaultStripKey && !stripIsExiting;
  let t11;
  let t12;
  if ($[15] !== completedInitialStripKey || $[16] !== defaultStripKey || $[17] !== loading || $[18] !== renderedStripKey || $[19] !== stripIsExiting) {
    t11 = () => {
      if (loading || !defaultStripKey) {
        setCompletedInitialStripKey(undefined);
        return;
      }
      if (renderedStripKey !== defaultStripKey || stripIsExiting) {
        return;
      }
      if (completedInitialStripKey === defaultStripKey) {
        return;
      }
      const timeoutId = window.setTimeout(() => setCompletedInitialStripKey(defaultStripKey), 420);
      return () => window.clearTimeout(timeoutId);
    };
    t12 = [completedInitialStripKey, defaultStripKey, loading, renderedStripKey, stripIsExiting];
    $[15] = completedInitialStripKey;
    $[16] = defaultStripKey;
    $[17] = loading;
    $[18] = renderedStripKey;
    $[19] = stripIsExiting;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useEffect(t11, t12);
  let t13;
  if ($[22] !== closeVisibleStrip || $[23] !== onClick || $[24] !== onClose || $[25] !== onTextClick || $[26] !== openStrip) {
    t13 = (key, isStripFilter, isTextFilter, isStripMounted, isPopoverOpen, event) => {
      if (isTextFilter) {
        onTextClick(key, event);
        return;
      }
      if (isStripFilter) {
        if (isStripMounted) {
          closeVisibleStrip();
          return;
        }
        openStrip(key);
        return;
      }
      if (isPopoverOpen) {
        onClose([], event);
        return;
      }
      onClick([key], event);
    };
    $[22] = closeVisibleStrip;
    $[23] = onClick;
    $[24] = onClose;
    $[25] = onTextClick;
    $[26] = openStrip;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const handleFilterClick = t13;
  let t14;
  if ($[28] !== buttonProperties) {
    t14 = () => {
      buttonProperties.onClick?.();
    };
    $[28] = buttonProperties;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const handleAllFiltersClick = t14;
  let t15;
  if ($[30] !== buttonProperties || $[31] !== handleAllFiltersClick || $[32] !== hasFilter) {
    t15 = hasFilter ? <Button className="!shrink-0 !whitespace-nowrap" {...buttonProperties} onClick={handleAllFiltersClick} /> : null;
    $[30] = buttonProperties;
    $[31] = handleAllFiltersClick;
    $[32] = hasFilter;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const allFiltersButton = t15;
  const t16 = `${classname || ""} relative flex min-w-0 flex-col`;
  const t17 = isNewPlP && (loading && showStripLoadingSkeleton || shouldReserveInitialStripHeight) ? 102 : undefined;
  let t18;
  if ($[34] !== t17) {
    t18 = {
      minHeight: t17
    };
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== allFiltersButton || $[37] !== children || $[38] !== chips || $[39] !== defaultStripKey || $[40] !== entryDirection || $[41] !== getItemStaggerProperties || $[42] !== handleFilterClick || $[43] !== handleTransitionEnd || $[44] !== hidePopoverActions || $[45] !== horizontalDragScrollProperties || $[46] !== isMobile || $[47] !== isNewPlP || $[48] !== isResetDisabled || $[49] !== loading || $[50] !== onClose || $[51] !== onReset || $[52] !== onSubmit || $[53] !== renderedStripKey || $[54] !== shouldAnimateStripItems || $[55] !== shouldAnimateStripTrigger || $[56] !== showStripLoadingSkeleton || $[57] !== stripIsExiting) {
    t19 = loading && isNewPlP ? <div className={`flex min-w-0 flex-col flex-nowrap overflow-hidden ${showStripLoadingSkeleton ? "h-[102px] gap-[14px]" : "h-9"}`}><div className="scrollbar-none flex min-w-0 flex-row gap-2 overflow-x-auto">{FILTER_CHIP_SKELETON_ITEM_KEYS.map(_temp)}</div>{showStripLoadingSkeleton && <div className="flex min-w-0 flex-row gap-6 overflow-x-clip pt-1">{STRIP_SKELETON_ITEM_KEYS.map((itemKey_0, index) => <div key={itemKey_0} {...shouldAnimateStripItems ? getItemStaggerProperties(itemKey_0, index, {
          className: "shrink-0"
        }) : {
          className: "shrink-0"
        }}><Skeleton shape="rectangle" height={48} width={64} /></div>)}</div>}</div> : <><div className="scrollbar-none flex min-w-0 cursor-grab select-none flex-row flex-nowrap gap-2 overflow-x-auto active:cursor-grabbing lg:cursor-auto lg:select-auto lg:overflow-visible" {...horizontalDragScrollProperties}>{!defaultStripKey && allFiltersButton}{chips?.map(chip => {
          const isStripVisible = chip.isStripFilter && renderedStripKey === chip.key && !stripIsExiting;
          return <Fragment key={chip.key}><div className={getStripFilterContainerClassName(chip.isStripFilter, shouldAnimateStripTrigger)}><Button StartIcon={chip.icon} EndIcon={chip.isStripFilter ? <MaterialIcon className="text-on-surface-medium-emphasis" name={isStripVisible ? "arrow_drop_up" : "arrow_drop_down"} /> : chip.hasValue ? <MaterialIcon className="text-body-1 relative z-[9] cursor-pointer" name="cancel" onClick={event_0 => onClose(chip.resetKeys, event_0)} variant="fill" /> : undefined} className={getStripFilterButtonClassName(chip.isStripFilter)} color={chip.hasValue ? "secondary" : "neutral"} iconColorMode={chip.hasValue ? "inherit" : "custom"} onClick={event_1 => handleFilterClick(chip.key, chip.isStripFilter, chip.isTextFilter, isStripVisible, chip.isOpen, event_1)} size="small" text={chip.title} variant="round-outline" />{!chip.isStripFilter && !chip.isTextFilter && <MiniPopover defaultHorizontalPosition="start" className={`shadow-2 overflow-hidden rounded-xl ${chip.shouldExpandPopover ? "lg:!w-[440px]" : ""}`} onClose={() => onClose([])} open={chip.isOpen} title={chip.popoverTitle} usePortal={isMobile}><div className="flex !w-full flex-col px-4 pb-4">{chip.selectedChild}{!hidePopoverActions && <div className="flex w-full flex-row items-center justify-between md:hidden"><Button className="w-[140px]" color="primary" onClick={onReset} text={"\u067E\u0627\u06A9 \u06A9\u0631\u062F\u0646"} variant="text" disabled={isResetDisabled} /><Button className="w-[140px]" color="secondary" onClick={onSubmit} text={"\u062A\u0627\u06CC\u06CC\u062F \u0627\u0646\u062A\u062E\u0627\u0628\u200C\u0647\u0627"} variant="contained" /></div>}</div></MiniPopover>}</div>{chip.key === defaultStripKey && allFiltersButton}</Fragment>;
        })}</div><StripFilterPanel entryDirection={entryDirection} isExiting={stripIsExiting} onTransitionEnd={handleTransitionEnd} renderedStripKey={renderedStripKey}>{children}</StripFilterPanel></>;
    $[36] = allFiltersButton;
    $[37] = children;
    $[38] = chips;
    $[39] = defaultStripKey;
    $[40] = entryDirection;
    $[41] = getItemStaggerProperties;
    $[42] = handleFilterClick;
    $[43] = handleTransitionEnd;
    $[44] = hidePopoverActions;
    $[45] = horizontalDragScrollProperties;
    $[46] = isMobile;
    $[47] = isNewPlP;
    $[48] = isResetDisabled;
    $[49] = loading;
    $[50] = onClose;
    $[51] = onReset;
    $[52] = onSubmit;
    $[53] = renderedStripKey;
    $[54] = shouldAnimateStripItems;
    $[55] = shouldAnimateStripTrigger;
    $[56] = showStripLoadingSkeleton;
    $[57] = stripIsExiting;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== t16 || $[60] !== t18 || $[61] !== t19) {
    t20 = <div className={t16} style={t18}>{t19}</div>;
    $[59] = t16;
    $[60] = t18;
    $[61] = t19;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  return t20;
};
export const HotelFilterChips = Component;
function _temp(itemKey) {
  return <Skeleton shape="rectangle" className="shrink-0 rounded-full" height={36} key={itemKey} width={120} />;
}